<template>
   <div class="passkey-entry-container">
        <div class="passkey-content">
            <div class="passkey-content-title"> 
                <span v-if="!editNameMode"> {{ passkeyName }} </span>
                <span v-else :style="{'font-weight': 'normal'}"> 
                    <OneInput
                        id="passkeyNewName"
                        :brandClass="brandClass()"
                        :t="$t"
                        :profileStyle="true"
                        v-model="form.passkeyNewName"
                        :form="v$.form"
                        :showErrorEvenIfParamIsNotDirty="true"
                        :paramName="'passkeyNewName'"
                        @blur="touchForm('passkeyNewName')"
                        @keyup="clearError()"
                        :errors="[
                            {message: '', thereIsTheError:v$.form.passkeyNewName.$invalid},
                        ]"
                    ></OneInput>
                 </span>
                <span v-if="passkeyCreationDate"> - </span> 
                <span v-if="passkeyCreationDate"> {{ passkeyCreationDate }} </span>
            </div>
            <div v-if="passkeyCreated"> {{ $t('profile_account_data_passkey_entry_subtitle_prefix') + ": " + passkeyCreated }}</div>
        </div>
        <div class="passkey-actions">
            <div class="not-edit-mode" v-if="!editNameMode">
                <div @keydown="ariaClick" role="button" tabindex="0" @click="enterEditMode" class="passkey-rename-icon" :class="brandClass()"></div>
                <div @keydown="ariaClick" role="button" tabindex="0" @click="openPopup" class="passkey-delete-icon" :class="brandClass()"></div>
            </div>
            <div class="edit-mode" v-else>
                <div @keydown="ariaClick" role="button" tabindex="0" @click="renamePasskey" class="check-icon" :class="brandClass() + ' ' + (this.v$.form.passkeyNewName.$invalid ? 'disabled' : '')"></div>
                <div @keydown="ariaClick" role="button" tabindex="0" @click="closePasskeyRenaming" class="close-icon" :class="brandClass()"></div>
            </div>
        </div>
        <OneAlert
        v-if="showPopup"
        :brandClass="brandClass()"
        :title="$t('profile_account_data_passkey_delete_title')"
        :content="$t('profile_account_data_passkey_delete_content')"
        :button_confirm_label="$t('profile_delete_button')"
        :button_cancel_label="$t('profile_cancel_button')"
        @confirm="deletePasskey"
        @close="closePopup"
        />
   </div>
</template>
<script>
import oneUtils from "../../../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import { defineComponent } from 'vue';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default defineComponent({
  inject:['$c', '$t', 'brandClass'],
  mixins: [
    oneUtils.accessibilityMixin, 
    oneUtils.customValidationMixin
  ],
  emits: ["getPasskeys", "editNameMode", "closeEditModeForPasskeyEntryWithUuid"],
  name: "PasskeyEntry",
  props: {
    passkeyEntry: {
        type: Object
    },
    passkeyEntryUuidInEditMode: {
        type: String
    },
    passkeyUuidToClose: {
        type: String
    }
  },
  setup: () => {
    return {v$: useVuelidate()}
  },  
  validations: {
    form: {
        passkeyNewName: {
            passkeyNameRegexValidation: function (value) {
            return this.passkeyNameRegexValidation(value);
            }
        }
    }
  },
  data: () =>({
    oneMoment: null,
    form: {
      passkeyNewName: "",
    },
    editNameMode: false,
    showPopup: false
  }),
  computed: {
    passkeyName() {
        return this.passkeyEntry.deviceName.split("__")[0];
    },
    passkeyTimestamp() {
        if(this.passkeyEntry.deviceName.indexOf("__") == -1)
            return null;

        const timestamp = parseInt(this.passkeyEntry.deviceName.split("__")[1]);

        if(timestamp === NaN)
            return null;

        return timestamp;
    },
    passkeyCreationDate() {
        if(!this.oneMoment || !this.passkeyTimestamp) return "";
    
        return this.oneMoment.formatFromTo(
            this.passkeyTimestamp, 
            "", //formatFrom is empty string because the format is a timestamp
            this.$c("_profileConfiguration").personalDataSection.birthday.dateFormat)
    },
    passkeyCreated() {
       if(!this.oneMoment || !this.passkeyTimestamp) return "";

        const currentTimestamp = this.oneMoment.getCurrentTimestampWithMilliseconds();

        const diffMs = Math.abs(currentTimestamp - this.passkeyTimestamp);
        const diffMin = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMin / 60 ); 
        const diffDays = Math.floor(diffHours / 24 ); 
        const diffMonths = Math.floor(diffDays / 30 ); 
        const diffYears = Math.floor(diffDays / 365 ); 

        if(diffYears !== 0)
            return diffYears.toString() + " " + (diffYears === 1 ? this.$t("profile_account_data_passkey_entry_subtitle_year") : this.$t("profile_account_data_passkey_entry_subtitle_years")) + " " + this.$t("profile_account_data_passkey_entry_subtitle_ago");
        if(diffDays !== 0)
            return diffDays.toString() + " " + (diffDays === 1 ? this.$t("profile_account_data_passkey_entry_subtitle_day") : this.$t("profile_account_data_passkey_entry_subtitle_days")) + " " + this.$t("profile_account_data_passkey_entry_subtitle_ago");
        if(diffHours !== 0)
            return diffHours.toString() + " " + (diffHours === 1 ? this.$t("profile_account_data_passkey_entry_subtitle_hour") : this.$t("profile_account_data_passkey_entry_subtitle_hours")) + " " + this.$t("profile_account_data_passkey_entry_subtitle_ago");
        if(diffMin !== 0)
            return diffMin.toString() + " " + (diffMin === 1 ? this.$t("profile_account_data_passkey_entry_subtitle_minute") : this.$t("profile_account_data_passkey_entry_subtitle_minutes")) + " " + this.$t("profile_account_data_passkey_entry_subtitle_ago");

        return this.$t("profile_account_data_passkey_entry_subtitle_just_now");

    },
    passkeyUuid(){
        return this.passkeyEntry.uuid;
    }
  },
  methods: {
    enterEditMode() {
        if(this.passkeyEntry.uuid && this.passkeyEntry.uuid !== this.passkeyEntryUuidInEditMode) this.$emit('closeEditModeForPasskeyEntryWithUuid', this.passkeyEntryUuidInEditMode);
        this.$emit('editNameMode', {value: true, uuid: this.passkeyEntry.uuid});
        this.editNameMode = true
    },
    deletePasskey() {
        oneUtils.httpClient.deletePasskey(
            this.passkeyUuid, window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token
        ).then(() => {
            oneUtils.utils.showMessage("SUCCESS", this.$t("profile_user_success"));
            this.$emit("getPasskeys")
        }).catch(() => {
            oneUtils.utils.showMessage("ERROR", this.$t("generic_system_error_personal"));
        }).finally(() => {
            this.closePopup();
        })
    },
    renamePasskey() {
        if(this.v$.form.passkeyNewName.$invalid) return;

        oneUtils.httpClient.renamePasskey(
            this.passkeyUuid, this.form.passkeyNewName + "__" + this.passkeyTimestamp, window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token
        ).then(() => {
            oneUtils.utils.showMessage("SUCCESS", this.$t("profile_user_success"));
            this.$emit("getPasskeys")
        }).catch(() => {
            this.form.passkeyNewName = this.passkeyName;
            oneUtils.utils.showMessage("ERROR", this.$t('generic_system_error_personal'));
        })
        this.editNameMode = false;
        this.$emit('editNameMode', {value: false, uuid: null});
    },
    closePasskeyRenaming() {
        this.$emit('editNameMode', {value: false, uuid: null});
        this.editNameMode = false;
        this.form.passkeyNewName = this.passkeyName;
    },
    openPopup() {
        this.showPopup = true;
    },
    closePopup() {
        this.showPopup = false;
    },
  },
  watch: {
    passkeyUuidToClose: {
      handler(newVal, oldVal) {
        if(newVal === this.passkeyEntry.uuid) {
            this.editNameMode = false;
            this.form.passkeyNewName = this.passkeyName;
        }
      },
    },
  },
  async mounted() {
    this.form.passkeyNewName = this.passkeyName;

    const awaitImport = await import('../../../../../../gcdm-one-library/gcdm-one-moment/src/bootstrap');
    this.oneMoment = (awaitImport).default;
  }
});
</script>
<style scoped lang="scss">
.passkey-entry-container {
    padding: 24px;
    display: flex;
    justify-content: space-between;
    .passkey-content {
        width: 100%;
        margin-right: 15px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        .passkey-content-title {
            font-weight: bold;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 5px;
        }
    }
    }
    .passkey-actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        > div {
            display: flex;
            flex-direction: row;
            justify-content: center;
            gap: 16px;
            align-items: center;
            &.edit-mode {
                margin-bottom: 28px;
            }
            .passkey-delete-icon {
                width: 24px;
                height: 24px;
                cursor: pointer;
                background-image: url("@/assets/default/light/delete-passkey-light.svg");

                &.__d {
                    background-image: url("@/assets/default/dark/delete-passkey-dark.svg");
                }
            }
            .passkey-rename-icon {
                width: 24px;
                height: 24px;
                cursor: pointer;
                background-image: url("@/assets/default/light/rename-passkey-light.svg");

                &.__d {
                    background-image: url("@/assets/default/dark/rename-passkey-dark.svg");
                }
            }
            .check-icon {
                margin-top: 3px;
                width: 24px;
                height: 24px;
                background-repeat: no-repeat;
                background-size: 24px;
                cursor: pointer;
                &.disabled {
                    cursor: auto;
                }
                background-image: url("@/assets/default/light/check2-default-light.svg");

                &.__d {
                    background-image: url("@/assets/default/dark/check2-default-dark.svg");
                }
            }
            .close-icon {
                width: 24px;
                height: 24px;
                cursor: pointer;
                background-image: url("@/assets/default/light/close-default-light.svg");

                &.__d {
                    background-image: url("@/assets/default/dark/close-default-dark.svg");
                }
            }
        }
    }
</style>
